import React, { useMemo, useState } from 'react';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  useGetBookingSettlementQuery,
  useBookingsSettleForAccountingMutation,
  useBookingsUnsettleForAccountingMutation,
} from '../Service/bookingSettlementApi';
import { formatBookingCurrency } from '../Helpers/formatBookingCurrency';
import { getBookingSettlementErrorMessage } from '../bookingSettlementErrorMapping';

interface BookingSettlementPanelProps {
  bookingId: number;
}

interface FeedbackState {
  severity: 'success' | 'error';
  message: string;
}

const normalizeInvoiceNumbers = (invoiceNumbers: string[] | null | undefined): string[] => {
  if (!Array.isArray(invoiceNumbers)) {
    return [];
  }

  return invoiceNumbers
    .filter((invoiceNumber): invoiceNumber is string => typeof invoiceNumber === 'string')
    .map((invoiceNumber) => invoiceNumber.trim())
    .filter(Boolean);
};

const formatDraftPurchaseInvoiceMessage = (
  baseMessage: string,
  invoiceNumbers: string[]
): string => {
  return `${baseMessage}. Dokumenty: ${invoiceNumbers.join(', ')}.`;
};

const formatDateLabel = (value: string | null | undefined): string => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleString('pl-PL');
};

const BookingSettlementPanel: React.FC<BookingSettlementPanelProps> = ({ bookingId }) => {
  const [isUnsettledDialogOpen, setIsUnsettledDialogOpen] = useState(false);
  const [unsettledReason, setUnsettledReason] = useState('');
  const [reasonValidationError, setReasonValidationError] = useState('');
  const [unsettleSubmitError, setUnsettleSubmitError] = useState('');
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetBookingSettlementQuery(bookingId, {
    skip: !Number.isFinite(bookingId) || bookingId <= 0,
    refetchOnMountOrArgChange: true,
  });
  const [settleBookingForAccounting, { isLoading: isSettling }] =
    useBookingsSettleForAccountingMutation();
  const [unsettleBookingForAccounting, { isLoading: isUnsettling }] =
    useBookingsUnsettleForAccountingMutation();

  const isActionInProgress = isFetching || isSettling || isUnsettling;
  const hasManualReviewPurchaseInvoiceDrafts =
    data?.hasDraftPurchaseInvoicesRequiringManualReview ?? false;
  const hasSupplierApprovalPendingPurchaseInvoiceDrafts =
    data?.hasSupplierApprovalPendingPurchaseInvoices ?? false;
  const manualReviewPurchaseInvoiceNumbers = normalizeInvoiceNumbers(
    data?.draftPurchaseInvoiceNumbersRequiringManualReview
  );
  const supplierApprovalPendingPurchaseInvoiceNumbers = normalizeInvoiceNumbers(
    data?.supplierApprovalPendingPurchaseInvoiceNumbers
  );
  const hasManualReviewPurchaseInvoiceDraftDiagnostics =
    hasManualReviewPurchaseInvoiceDrafts && manualReviewPurchaseInvoiceNumbers.length > 0;
  const hasSupplierApprovalPendingPurchaseInvoiceDraftDiagnostics =
    hasSupplierApprovalPendingPurchaseInvoiceDrafts &&
    supplierApprovalPendingPurchaseInvoiceNumbers.length > 0;
  const shouldShowGenericPurchaseInvoiceDraftFallback =
    Boolean(data?.hasDraftPurchaseInvoices) &&
    !hasManualReviewPurchaseInvoiceDraftDiagnostics &&
    !hasSupplierApprovalPendingPurchaseInvoiceDraftDiagnostics;
  const manualReviewPurchaseInvoiceDraftMessage = formatDraftPurchaseInvoiceMessage(
    'Rozliczenie jest zablokowane, ponieważ część faktur zakupowych w statusie Draft wymaga ręcznej weryfikacji danych',
    manualReviewPurchaseInvoiceNumbers
  );
  const supplierApprovalPendingPurchaseInvoiceDraftMessage = formatDraftPurchaseInvoiceMessage(
    'Rozliczenie jest zablokowane, ponieważ część faktur zakupowych w statusie Draft oczekuje na zatwierdzenie dostawcy',
    supplierApprovalPendingPurchaseInvoiceNumbers
  );
  const statusLabel = useMemo(() => {
    if (!data?.isSettledForAccounting) {
      return 'Nierozliczone';
    }

    const settledBy = data.settledByUserDisplayName?.trim() || 'nieznanego użytkownika';
    return `Rozliczone ${formatDateLabel(data.settledAtUtc)} przez ${settledBy}`;
  }, [data]);

  const handleSettle = async () => {
    if (!data) {
      return;
    }

    try {
      setFeedback(null);
      await settleBookingForAccounting(bookingId).unwrap();
      setFeedback({
        severity: 'success',
        message: 'Termin został rozliczony dla księgowości.',
      });
    } catch (settleError) {
      setFeedback({
        severity: 'error',
        message: getBookingSettlementErrorMessage(
          settleError,
          'Nie udało się rozliczyć terminu dla księgowości.'
        ),
      });
    }
  };

  const handleOpenUnsettleDialog = () => {
    setFeedback(null);
    setReasonValidationError('');
    setUnsettleSubmitError('');
    setUnsettledReason('');
    setIsUnsettledDialogOpen(true);
  };

  const handleCloseUnsettleDialog = () => {
    if (isUnsettling) {
      return;
    }

    setIsUnsettledDialogOpen(false);
    setReasonValidationError('');
    setUnsettleSubmitError('');
  };

  const handleConfirmUnsettle = async () => {
    const normalizedReason = unsettledReason.trim();

    if (!normalizedReason) {
      setReasonValidationError('Powód cofnięcia rozliczenia jest wymagany.');
      return;
    }

    try {
      setFeedback(null);
      setReasonValidationError('');
      setUnsettleSubmitError('');
      await unsettleBookingForAccounting({
        bookingId,
        reason: normalizedReason,
      }).unwrap();
      setFeedback({
        severity: 'success',
        message: 'Rozliczenie księgowe terminu zostało cofnięte.',
      });
      setIsUnsettledDialogOpen(false);
      setUnsettledReason('');
    } catch (unsettleError) {
      setUnsettleSubmitError(
        getBookingSettlementErrorMessage(
          unsettleError,
          'Nie udało się cofnąć rozliczenia księgowego terminu.'
        )
      );
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Alert severity="error">
        {getBookingSettlementErrorMessage(
          error,
          'Nie udało się pobrać statusu rozliczenia księgowego.'
        )}
      </Alert>
    );
  }

  return (
    <>
      <Stack spacing={2.5}>
        {feedback && <Alert severity={feedback.severity}>{feedback.message}</Alert>}

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <AccountBalanceWalletIcon color="primary" />
              <Typography variant="h6">Status rozliczenia</Typography>
            </Stack>
            <Chip
              label={statusLabel}
              color={data.isSettledForAccounting ? 'success' : 'default'}
              variant={data.isSettledForAccounting ? 'filled' : 'outlined'}
              sx={{ alignSelf: 'flex-start' }}
            />
            {data.sentToAccountingAtUtc && (
              <Typography variant="body2" color="text.secondary">
                Wysłano do księgowości: {formatDateLabel(data.sentToAccountingAtUtc)}
              </Typography>
            )}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              variant="contained"
              onClick={handleSettle}
              disabled={isActionInProgress || data.isSettledForAccounting || !data.canSettle}
            >
              {isSettling ? 'Trwa rozliczanie...' : 'Rozlicz termin'}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleOpenUnsettleDialog}
              disabled={isActionInProgress || !data.isSettledForAccounting}
            >
              {isUnsettling ? 'Trwa cofanie...' : 'Cofnij rozliczenie'}
            </Button>
          </Stack>
        </Stack>

        {!data.isSettledForAccounting && !data.canSettle && (
          <Alert severity="info">
            Termin nie spełnia obecnie warunków rozliczenia księgowego. Sprawdź, czy wszystkie
            wymagane dokumenty i płatności zostały poprawnie wprowadzone, a następnie spróbuj
            ponownie.
          </Alert>
        )}

        {data.hasDraftSalesInvoices && (
          <Alert severity="warning">
            Rozliczenie jest zablokowane, ponieważ istnieją faktury sprzedażowe w statusie Draft.
          </Alert>
        )}

        {hasManualReviewPurchaseInvoiceDraftDiagnostics && (
          <Alert severity="warning">{manualReviewPurchaseInvoiceDraftMessage}</Alert>
        )}

        {hasSupplierApprovalPendingPurchaseInvoiceDraftDiagnostics && (
          <Alert severity="warning">{supplierApprovalPendingPurchaseInvoiceDraftMessage}</Alert>
        )}

        {shouldShowGenericPurchaseInvoiceDraftFallback && (
          <Alert severity="warning">
            Rozliczenie jest zablokowane, ponieważ istnieją faktury zakupowe w statusie Draft.
          </Alert>
        )}

        {data.isSettledForAccounting && (
          <>
            <Divider />

            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(4, minmax(0, 1fr))',
                },
              }}
            >
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Suma FV sprzedażowych
                </Typography>
                <Typography variant="h6">{formatBookingCurrency(data.salesGrossTotal)}</Typography>
              </Box>

              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Suma FZ przypisanych
                </Typography>
                <Typography variant="h6">{formatBookingCurrency(data.purchaseAllocatedTotal)}</Typography>
              </Box>

              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Marża
                </Typography>
                <Typography variant="h6">{formatBookingCurrency(data.marginTotal)}</Typography>
              </Box>

              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Rozliczono
                </Typography>
                <Typography variant="h6">{formatDateLabel(data.settledAtUtc)}</Typography>
              </Box>
            </Box>
          </>
        )}
      </Stack>

      <Dialog
        open={isUnsettledDialogOpen}
        onClose={handleCloseUnsettleDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Cofnij rozliczenie księgowe</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              Cofnięcie rozliczenia usuwa snapshot księgowy i odblokowuje termin do ponownego
              rozliczenia.
            </Alert>

            {unsettleSubmitError && <Alert severity="error">{unsettleSubmitError}</Alert>}

            <TextField
              label="Powód cofnięcia"
              value={unsettledReason}
              onChange={(event) => {
                setUnsettledReason(event.target.value);
                if (reasonValidationError) {
                  setReasonValidationError('');
                }
                if (unsettleSubmitError) {
                  setUnsettleSubmitError('');
                }
              }}
              required
              multiline
              minRows={3}
              inputProps={{ maxLength: 500 }}
              error={Boolean(reasonValidationError)}
              helperText={reasonValidationError || 'Pole wymagane. Maksymalnie 500 znaków.'}
              disabled={isUnsettling}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnsettleDialog} disabled={isUnsettling}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmUnsettle}
            disabled={isUnsettling}
          >
            {isUnsettling ? 'Trwa cofanie...' : 'Potwierdź cofnięcie'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BookingSettlementPanel;
